import React, { useEffect, useState } from 'react';
import { Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';

/**
 * Prévisualisation d'un document (PDF ou image) dans un modal.
 * Source acceptée :
 *   - `file` : un objet File (fichier local, prévisualisation avant upload)
 *   - `blob` : un Blob déjà téléchargé (depuis l'API GED)
 * Le composant crée/révoque automatiquement l'object URL.
 */
export default function DocumentPreviewModal({
  open,
  onClose,
  title,
  file = null,
  blob = null,
  mimeType = null,
  fileName = null,
}) {
  const { t } = useTranslation('commun');
  const [objectUrl, setObjectUrl] = useState(null);
  const [resolvedMime, setResolvedMime] = useState(null);

  useEffect(() => {
    if (!open) {
      setObjectUrl(null);
      setResolvedMime(null);
      return;
    }
    const source = file || blob;
    if (!source) return;
    const mime = mimeType || source.type || guessMimeFromName(fileName || source.name || '');
    const url = URL.createObjectURL(source);
    setObjectUrl(url);
    setResolvedMime(mime);
    return () => URL.revokeObjectURL(url);
  }, [open, file, blob, mimeType, fileName]);

  const isPdf = resolvedMime?.includes('pdf');
  const isImage = resolvedMime?.startsWith('image/');

  return (
    <CBModal
      title={title === undefined ? t('documentPreviewModal.titre') : title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      styles={{ body: { padding: 0, height: '75vh', background: '#1f2937' } }}
    >
      {!objectUrl ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spin />
        </div>
      ) : isPdf ? (
        <iframe
          src={objectUrl}
          title={fileName || 'PDF'}
          style={{ width: '100%', height: '100%', border: 0 }}
        />
      ) : isImage ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 16 }}>
          <img
            src={objectUrl}
            alt={fileName || t('documentPreviewModal.document')}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#fff' }}>
          <Empty
            description={
              <span style={{ color: '#fff' }}>
                {t('documentPreviewModal.formatNonPrevisualisable', { format: resolvedMime || t('documentPreviewModal.inconnu') })}
              </span>
            }
          />
        </div>
      )}
    </CBModal>
  );
}

function guessMimeFromName(name) {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  return null;
}
